/**
 * F-users:edit — comprehensive super-admin user editor.
 *
 * The "edit everything" form for a consumer user's core fields, rendered inside
 * a Drawer on the platform-user detail page (super-admin only). Writes through
 * `PATCH /admin/platform-users/:id/fields` (the `editFields` endpoint), which is
 * If-Match-guarded on `users.updated_at`, audited to `user_admin_audit`, and
 * runs the suspend/restore cascade for status transitions to/from 'suspended'.
 * Only CHANGED fields are sent. The avatar is managed inline via the dedicated
 * presigned-upload + finalize endpoints.
 */
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Avatar,
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  Dialog,
  FormField,
  Select,
  TextInput,
  useToast,
} from '@bravemove/ui-web';
import {
  USER_ROLES,
  type AdminEditUserInput,
  type AdminUserDetail,
  type NameLockOverride,
  type UserRole,
} from '@bravemove/types';
import { ApiError } from '../../auth/types';
import { adminUsersApi } from '../../api/adminUsers';
import { translateApiError } from '../../auth/errors.js';
import { useVerificationPermissions } from '../../hooks/useVerificationPermissions';
import { NameLockOverridePanel } from '../verification/NameLockOverride';
import { isNameLockedError } from '../verification/verificationErrors';

const STATUS_VALUES = [
  'pending_verification',
  'active',
  'suspended',
  'disabled',
  'deleted',
] as const;
const LOCALE_VALUES = ['en', 'ar', 'fr'] as const;

function rolesEqual(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

interface UserEditFormProps {
  readonly detail: AdminUserDetail;
  /** Updated detail after a successful save / avatar change. */
  readonly onSaved: (next: AdminUserDetail) => void;
  /** Full reload of the detail (used after a stale conflict). */
  readonly onReload: () => void;
}

export function UserEditForm({ detail, onSaved, onReload }: UserEditFormProps) {
  const { t } = useI18n();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  // Initial snapshot for change-detection — keyed on detail.updatedAt at the
  // call site so the form re-initialises after each successful save.
  const initial = useMemo(
    () => ({
      displayName: detail.displayName,
      email: detail.email,
      phone: detail.phone ?? '',
      roles: detail.roles as readonly UserRole[],
      status: detail.status,
      locale: detail.locale,
      countryCode: detail.countryCode,
    }),
    [detail],
  );

  const [displayName, setDisplayName] = useState(initial.displayName);
  const [email, setEmail] = useState(initial.email);
  const [phone, setPhone] = useState(initial.phone);
  const [roles, setRoles] = useState<readonly UserRole[]>(initial.roles);
  const [status, setStatus] = useState<string>(initial.status);
  const [locale, setLocale] = useState<string>(initial.locale);
  const [countryCode, setCountryCode] = useState<string>(initial.countryCode);

  const [saving, setSaving] = useState(false);
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [staleOpen, setStaleOpen] = useState(false);
  // F09 v2 — the last save hit a verified name (422 NAME_LOCKED_VERIFIED).
  const [nameLockError, setNameLockError] = useState<unknown>(null);
  const { canReview: canOverrideNameLock } = useVerificationPermissions();

  const toggleRole = (role: UserRole) => {
    setRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  };

  function collectChanges(): AdminEditUserInput {
    const fields: AdminEditUserInput = {};
    if (displayName.trim() !== initial.displayName) fields.displayName = displayName.trim();
    if (email.trim().toLowerCase() !== initial.email) fields.email = email.trim();
    const nextPhone = phone.trim() === '' ? null : phone.trim();
    if (nextPhone !== (initial.phone === '' ? null : initial.phone)) fields.phone = nextPhone;
    if (!rolesEqual(roles, initial.roles)) fields.roles = [...roles];
    if (status !== initial.status) fields.status = status as AdminEditUserInput['status'];
    if (locale !== initial.locale) fields.locale = locale as AdminEditUserInput['locale'];
    if (countryCode.trim() !== initial.countryCode) fields.countryCode = countryCode.trim();
    return fields;
  }

  const pending = collectChanges();
  const dirty = Object.keys(pending).length > 0;
  const rolesEmpty = roles.length === 0;

  const handleSave = async (nameLockOverride?: NameLockOverride) => {
    const fields = collectChanges();
    if (Object.keys(fields).length === 0) {
      toast.push({
        tone: 'info',
        title: t('admin:f43.edit.noChanges', { defaultValue: 'No changes to save.' }),
      });
      return;
    }
    if (rolesEmpty) {
      toast.push({
        tone: 'danger',
        title: t('admin:f43.edit.rolesRequired', {
          defaultValue: 'A user must have at least one role.',
        }),
      });
      return;
    }
    setSaving(true);
    try {
      const next = await adminUsersApi.editFields(
        detail.id,
        nameLockOverride ? { ...fields, nameLockOverride } : fields,
        detail.updatedAt,
      );
      setNameLockError(null);
      toast.push({
        tone: 'success',
        title: t('admin:f43.edit.saved', { defaultValue: 'User updated.' }),
      });
      onSaved(next);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'STALE_USER') setStaleOpen(true);
      else if (isNameLockedError(err)) setNameLockError(err);
      else toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setSaving(false);
    }
  };

  const onPickAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setAvatarSaving(true);
    try {
      const next = await adminUsersApi.uploadAvatar(detail.id, file);
      toast.push({
        tone: 'success',
        title: t('admin:f43.edit.avatarSaved', { defaultValue: 'Profile picture updated.' }),
      });
      onSaved(next);
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setAvatarSaving(false);
    }
  };

  const onRemoveAvatar = async () => {
    setAvatarSaving(true);
    try {
      const next = await adminUsersApi.removeAvatar(detail.id);
      toast.push({
        tone: 'success',
        title: t('admin:f43.edit.avatarRemoved', { defaultValue: 'Profile picture removed.' }),
      });
      onSaved(next);
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setAvatarSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="admin-user-edit-form">
      <Section title={t('admin:f43.edit.avatar', { defaultValue: 'Profile picture' })}>
        <div className="flex flex-wrap items-center gap-3">
          <Avatar name={detail.displayName} src={detail.avatarUrl ?? undefined} size="lg" />
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              loading={avatarSaving}
              onClick={() => fileRef.current?.click()}
              data-testid="admin-user-edit-avatar-upload"
            >
              {t('admin:f43.edit.avatarUpload', { defaultValue: 'Upload' })}
            </Button>
            {detail.avatarUrl ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={avatarSaving}
                onClick={() => void onRemoveAvatar()}
                data-testid="admin-user-edit-avatar-remove"
              >
                {t('admin:f43.edit.avatarRemove', { defaultValue: 'Remove' })}
              </Button>
            ) : null}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => void onPickAvatar(e)}
            data-testid="admin-user-edit-avatar-input"
          />
        </div>
      </Section>

      <Section title={t('admin:f43.edit.identity', { defaultValue: 'Identity' })}>
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField label={t('admin:f43.profile.displayName', { defaultValue: 'Display name' })}>
            <TextInput
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                // A new name needs a new lock decision.
                setNameLockError(null);
              }}
              data-testid="admin-user-edit-displayName"
            />
          </FormField>
          <FormField label={t('admin:f43.profile.email', { defaultValue: 'Email' })}>
            <TextInput
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-user-edit-email"
            />
          </FormField>
          <FormField label={t('admin:f43.profile.phone', { defaultValue: 'Phone' })}>
            <TextInput
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+961…"
              data-testid="admin-user-edit-phone"
            />
          </FormField>
          <FormField label={t('admin:f43.profile.locale', { defaultValue: 'Locale' })}>
            <Select
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              options={LOCALE_VALUES.map((l) => ({ value: l, label: l }))}
              data-testid="admin-user-edit-locale"
            />
          </FormField>
          <FormField label={t('admin:f43.profile.countryCode', { defaultValue: 'Country' })}>
            <TextInput
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value.toUpperCase())}
              maxLength={2}
              data-testid="admin-user-edit-country"
            />
          </FormField>
        </div>
      </Section>

      <Section title={t('admin:f43.edit.access', { defaultValue: 'Roles & status' })}>
        <FormField label={t('admin:f43.edit.roles', { defaultValue: 'Roles' })}>
          <div className="flex flex-wrap gap-3">
            {USER_ROLES.map((r) => (
              <Checkbox
                key={r}
                checked={roles.includes(r)}
                onChange={() => toggleRole(r)}
                label={t(`admin:f43.role.${r}`, { defaultValue: r })}
                data-testid={`admin-user-edit-role-${r}`}
              />
            ))}
          </div>
          {rolesEmpty ? (
            <p className="mt-1 text-[11px] text-danger">
              {t('admin:f43.edit.rolesRequired', {
                defaultValue: 'A user must have at least one role.',
              })}
            </p>
          ) : null}
        </FormField>
        <div className="mt-3 max-w-xs">
          <FormField label={t('admin:f43.profile.status', { defaultValue: 'Status' })}>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              options={STATUS_VALUES.map((s) => ({
                value: s,
                label: t(`moderation:user.status.${s}`, { defaultValue: s }),
              }))}
              data-testid="admin-user-edit-status"
            />
          </FormField>
          {status === 'suspended' && initial.status !== 'suspended' ? (
            <Banner tone="warning" className="mt-2">
              {t('admin:f43.edit.suspendWarning', {
                defaultValue:
                  'Setting status to suspended signs the user out everywhere and suspends any account they solely own.',
              })}
            </Banner>
          ) : null}
        </div>
      </Section>

      {nameLockError !== null ? (
        <NameLockOverridePanel
          error={nameLockError}
          canOverride={canOverrideNameLock}
          busy={saving}
          onOverride={(override) => void handleSave(override)}
          onDismiss={() => setNameLockError(null)}
          testId="admin-user-edit-name-lock"
        />
      ) : null}

      <div className="flex items-center justify-end gap-2 border-t border-hairline pt-3">
        <Badge tone="warning" size="sm">
          {t('admin:f43.edit.superAdminOnly', { defaultValue: 'Super-admin' })}
        </Badge>
        <Button
          variant="primary"
          loading={saving}
          disabled={!dirty || rolesEmpty}
          onClick={() => void handleSave()}
          data-testid="admin-user-edit-save"
        >
          {t('admin:f43.edit.save', { defaultValue: 'Save changes' })}
        </Button>
      </div>

      <Dialog
        open={staleOpen}
        onClose={() => setStaleOpen(false)}
        onConfirm={() => {
          setStaleOpen(false);
          onReload();
        }}
        title={t('admin:f43.edit.staleTitle', { defaultValue: 'User changed' })}
        confirmLabel={t('admin:f43.edit.staleReload', { defaultValue: 'Reload' })}
        cancelLabel={t('admin:common.cancel', { defaultValue: 'Cancel' })}
      >
        <p className="text-[13px] text-ink-2" data-testid="admin-user-edit-stale">
          {t('admin:f43.edit.staleBody', {
            defaultValue:
              'This user was modified since you opened the editor. Reload to get the latest before editing.',
          })}
        </p>
      </Dialog>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <h3 className="mb-1.5 text-[13px] font-semibold text-ink">{title}</h3>
      {children}
    </Card>
  );
}
